import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

const Gallery = () => {
  const navigate = useNavigate();

  // Placeholder images - will be replaced with actual photos
  const photos = Array.from({ length: 12 }, (_, i) => ({
    id: i + 1,
    title: `Photo ${i + 1}`,
    placeholder: true
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-6 h-6 text-teal-600" />
              <h1 className="text-xl font-bold text-gray-900">Our Gallery</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Our Gallery
            </h1>
            <p className="text-lg text-gray-600">
              Moments from our journey serving 1000+ clients across Madhya Pradesh
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div 
                  key={photo.id} 
                  className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                >
                  {photo.placeholder ? (
                    <div className="w-full h-full bg-gradient-to-br from-teal-100 to-teal-200 flex flex-col items-center justify-center">
                      <Upload className="w-12 h-12 text-teal-600 group-hover:scale-110 transition-transform mb-2" />
                      <span className="text-sm text-teal-700 font-medium">{photo.title}</span>
                    </div>
                  ) : (
                    <img 
                      src={photo.url} 
                      alt={photo.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-4 text-center">
                      {photo.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Upload Instructions */}
            <div className="mt-12 text-center bg-white rounded-xl shadow-md p-8 max-w-2xl mx-auto">
              <Upload className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Gallery Photos
              </h3>
              <p className="text-gray-600 mb-4">
                Photos can be uploaded by adding images to the server directory:<br />
                <code className="bg-gray-100 px-3 py-1 rounded text-sm text-teal-600 font-mono">
                  /app/frontend/public/images/gallery/
                </code>
              </p>
              <p className="text-sm text-gray-500">
                * Gallery images will be automatically displayed once uploaded
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Want to Work With Us?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join 1000+ satisfied clients who trust us for their financial and compliance needs
            </p>
            <Button
              size="lg"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-teal-600 hover:bg-teal-700 text-lg h-14 px-8"
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
